import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PawPrint, LogOut, Plus, Trash2 } from "lucide-react";

const TABS = ["Packs y precios", "Textos", "Imágenes y vídeos", "FAQ", "Integraciones", "Pedidos"];

const MEDIA_FIELDS = [
  ["hero_image", "Imagen principal (hero)"],
  ["hero_video", "Vídeo hero (URL mp4, vertical)"],
  ["problem_sofa", "Problema: sofá"],
  ["problem_clothes", "Problema: ropa negra"],
  ["problem_car", "Problema: coche"],
  ["demo_before", "Demo: foto ANTES"],
  ["demo_after", "Demo: foto DESPUÉS"],
  ["demo_video", "Demo: vídeo (URL mp4)"],
  ["use_sofa", "Uso: sofá"],
  ["use_bed", "Uso: cama"],
  ["use_clothes", "Uso: ropa"],
  ["use_car", "Uso: coche"],
];

const TEXT_FIELDS = [
  ["announcement_text", "Barra superior"],
  ["hero_headline", "Titular principal"],
  ["hero_subheadline", "Subtítulo"],
  ["hero_cta", "Texto botón hero"],
  ["final_cta_headline", "Titular CTA final"],
  ["final_cta_sub", "Subtítulo CTA final"],
  ["final_cta_button", "Botón CTA final"],
  ["social_proof_text", "Texto sección opiniones"],
  ["contact_email", "Email de contacto"],
  ["delivery_time", "Plazo de entrega (ej: 24/48h laborables)"],
];

const Input = ({ label, value, onChange, testId, type = "text", placeholder }) => (
  <label className="block">
    <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">{label}</span>
    <input
      data-testid={testId}
      type={type}
      value={value ?? ""}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1.5 w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
    />
  </label>
);

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem("pc_admin_token"));
  const [login, setLogin] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [config, setConfig] = useState(null);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    try {
      const { data } = await api.get("/admin/config", authHeaders);
      setConfig(data);
      const o = await api.get("/admin/orders", authHeaders);
      setOrders(o.data.orders);
    } catch (e) {
      setToken(null);
      localStorage.removeItem("pc_admin_token");
    }
  };

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const doLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const { data } = await api.post("/auth/login", login);
      localStorage.setItem("pc_admin_token", data.token);
      setToken(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setLoginError(typeof d === "string" ? d : "Error al iniciar sesión");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/config", config, authHeaders);
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error("Error al guardar");
    }
    setSaving(false);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4" data-testid="admin-login-page">
        <form onSubmit={doLogin} className="w-full max-w-sm bg-white rounded-3xl shadow-xl ring-1 ring-zinc-100 p-8">
          <span className="mx-auto w-11 h-11 rounded-full bg-brand flex items-center justify-center mb-5">
            <PawPrint className="w-5 h-5 text-white" />
          </span>
          <h1 className="font-display font-bold text-xl text-center">Panel PeloCero</h1>
          <div className="mt-6 space-y-4">
            <Input label="Email" testId="admin-email-input" value={login.email} onChange={(v) => setLogin({ ...login, email: v })} />
            <Input label="Contraseña" testId="admin-password-input" type="password" value={login.password} onChange={(v) => setLogin({ ...login, password: v })} />
          </div>
          {loginError && <p data-testid="admin-login-error" className="mt-3 text-sm text-red-600">{loginError}</p>}
          <button data-testid="admin-login-button" type="submit" className="mt-6 w-full bg-ink text-white font-display font-semibold rounded-full py-3 hover:bg-zinc-800 transition-colors">
            Entrar
          </button>
        </form>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-brand border-t-transparent animate-spin" />
      </div>
    );
  }

  const set = (key, value) => setConfig({ ...config, [key]: value });
  const setPack = (i, key, value) => {
    const packs = config.packs.map((p, idx) => (idx === i ? { ...p, [key]: value } : p));
    set("packs", packs);
  };
  const setFaq = (i, key, value) => set("faq", config.faq.map((f, idx) => (idx === i ? { ...f, [key]: value } : f)));

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-panel">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <span className="flex items-center gap-2 font-display font-bold">
            <span className="w-8 h-8 rounded-full bg-brand flex items-center justify-center">
              <PawPrint className="w-4 h-4 text-white" />
            </span>
            PeloCero · Configuración
          </span>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-save-button"
              onClick={save}
              disabled={saving}
              className="bg-brand text-white text-sm font-semibold rounded-full px-6 py-2.5 hover:bg-brand-dark disabled:opacity-60 transition-colors"
            >
              {saving ? "Guardando…" : "Guardar cambios"}
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={() => { localStorage.removeItem("pc_admin_token"); setToken(null); }}
              className="p-2.5 text-zinc-400 hover:text-ink"
              aria-label="Cerrar sesión"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-4 flex gap-1 overflow-x-auto pb-2">
          {TABS.map((t, i) => (
            <button
              key={t}
              data-testid={`admin-tab-${i}`}
              onClick={() => setTab(i)}
              className={`whitespace-nowrap text-sm font-medium rounded-full px-4 py-2 transition-colors ${tab === i ? "bg-ink text-white" : "text-zinc-500 hover:text-ink"}`}
            >
              {t}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {tab === 0 && (
          <div className="space-y-6" data-testid="admin-packs-tab">
            {config.packs.map((pack, i) => (
              <div key={pack.id} className="bg-white rounded-2xl ring-1 ring-zinc-200 p-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Input label={`Nombre (pack ${pack.units})`} testId={`admin-pack-name-${i}`} value={pack.name} onChange={(v) => setPack(i, "name", v)} />
                <Input label="Precio €" testId={`admin-pack-price-${i}`} type="number" value={pack.price} onChange={(v) => setPack(i, "price", parseFloat(v) || 0)} />
                <Input label="Envío €" testId={`admin-pack-shipping-${i}`} type="number" value={pack.shipping} onChange={(v) => setPack(i, "shipping", parseFloat(v) || 0)} />
                <Input label="Etiqueta" testId={`admin-pack-badge-${i}`} value={pack.badge} onChange={(v) => setPack(i, "badge", v)} />
                <Input label="Descripción corta" testId={`admin-pack-tagline-${i}`} value={pack.tagline} onChange={(v) => setPack(i, "tagline", v)} />
                <label className="flex items-center gap-2 text-sm mt-6">
                  <input
                    type="checkbox"
                    data-testid={`admin-pack-freeship-${i}`}
                    checked={!!pack.free_shipping}
                    onChange={(e) => setPack(i, "free_shipping", e.target.checked)}
                    className="w-4 h-4 accent-[#FF4D2D]"
                  />
                  Envío gratis
                </label>
              </div>
            ))}
          </div>
        )}

        {tab === 1 && (
          <div className="bg-white rounded-2xl ring-1 ring-zinc-200 p-6 space-y-5" data-testid="admin-texts-tab">
            {TEXT_FIELDS.map(([key, label]) => (
              <Input key={key} label={label} testId={`admin-text-${key}`} value={config[key]} onChange={(v) => set(key, v)} />
            ))}
          </div>
        )}

        {tab === 2 && (
          <div className="bg-white rounded-2xl ring-1 ring-zinc-200 p-6 space-y-5" data-testid="admin-media-tab">
            <p className="text-sm text-zinc-500">Pega aquí las URLs de tus fotos y vídeos. Los vídeos se reproducen en autoplay silenciado.</p>
            {MEDIA_FIELDS.map(([key, label]) => (
              <Input
                key={key}
                label={label}
                testId={`admin-media-${key}`}
                value={config.media?.[key]}
                placeholder="https://…"
                onChange={(v) => set("media", { ...config.media, [key]: v })}
              />
            ))}
          </div>
        )}

        {tab === 3 && (
          <div className="space-y-4" data-testid="admin-faq-tab">
            {config.faq.map((item, i) => (
              <div key={i} className="bg-white rounded-2xl ring-1 ring-zinc-200 p-6 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <Input label={`Pregunta ${i + 1}`} testId={`admin-faq-q-${i}`} value={item.q} onChange={(v) => setFaq(i, "q", v)} />
                  </div>
                  <button
                    data-testid={`admin-faq-remove-${i}`}
                    onClick={() => set("faq", config.faq.filter((_, idx) => idx !== i))}
                    className="mt-6 p-2 text-zinc-300 hover:text-red-500"
                    aria-label="Eliminar pregunta"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <label className="block">
                  <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">Respuesta</span>
                  <textarea
                    data-testid={`admin-faq-a-${i}`}
                    value={item.a}
                    onChange={(e) => setFaq(i, "a", e.target.value)}
                    rows={2}
                    className="mt-1.5 w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
                  />
                </label>
              </div>
            ))}
            <button
              data-testid="admin-faq-add"
              onClick={() => set("faq", [...config.faq, { q: "", a: "" }])}
              className="flex items-center gap-2 text-sm font-semibold text-brand hover:text-brand-dark"
            >
              <Plus className="w-4 h-4" /> Añadir pregunta
            </button>
          </div>
        )}

        {tab === 4 && (
          <div className="bg-white rounded-2xl ring-1 ring-zinc-200 p-6 space-y-5" data-testid="admin-integrations-tab">
            <Input label="Meta Pixel ID" testId="admin-meta-pixel" value={config.meta_pixel_id} onChange={(v) => set("meta_pixel_id", v)} />
            <Input label="Meta Conversions API — token de acceso" testId="admin-meta-capi" value={config.meta_capi_access_token} placeholder="Pegar token nuevo para reemplazar" onChange={(v) => set("meta_capi_access_token", v)} />
            <Input label="Versión Graph API" testId="admin-meta-version" value={config.meta_graph_api_version} onChange={(v) => set("meta_graph_api_version", v)} />
            <Input label="Google Analytics 4 — Measurement ID (G-…)" testId="admin-ga4" value={config.ga4_measurement_id} onChange={(v) => set("ga4_measurement_id", v)} />
            <Input label="Stripe — clave secreta (sk_live_…)" testId="admin-stripe-key" value={config.stripe_secret_key} placeholder="Pegar clave nueva para reemplazar" onChange={(v) => set("stripe_secret_key", v)} />
            <p className="text-xs text-zinc-400">Las claves se muestran enmascaradas. Si no cambias el campo, se conserva la clave actual.</p>
          </div>
        )}

        {tab === 5 && (
          <div className="bg-white rounded-2xl ring-1 ring-zinc-200 overflow-hidden" data-testid="admin-orders-tab">
            {orders.length === 0 ? (
              <p className="p-8 text-sm text-zinc-500 text-center">Aún no hay pedidos.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-zinc-400 uppercase tracking-wide border-b border-zinc-100">
                      <th className="px-5 py-3">Fecha</th>
                      <th className="px-5 py-3">Pack</th>
                      <th className="px-5 py-3">Importe</th>
                      <th className="px-5 py-3">Estado</th>
                      <th className="px-5 py-3">Cliente</th>
                      <th className="px-5 py-3">Dirección de envío</th>
                      <th className="px-5 py-3">UTM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((o, i) => {
                      const addr = o.shipping?.address || o.customer?.address;
                      const addrStr = addr ? [addr.line1, addr.line2, addr.postal_code, addr.city, addr.country].filter(Boolean).join(", ") : "";
                      return (
                      <tr key={o.order_id || i} className="border-b border-zinc-50 align-top" data-testid={`admin-order-${i}`}>
                        <td className="px-5 py-3 text-zinc-500 whitespace-nowrap">{new Date(o.created_at).toLocaleString("es-ES")}</td>
                        <td className="px-5 py-3 font-medium whitespace-nowrap">{o.units} × PeloCero</td>
                        <td className="px-5 py-3 font-semibold whitespace-nowrap">{Number(o.amount).toLocaleString("en-US", { style: "currency", currency: "USD" })}</td>
                        <td className="px-5 py-3">
                          <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${o.payment_status === "paid" ? "bg-green-50 text-green-700" : "bg-zinc-100 text-zinc-500"}`}>
                            {o.payment_status === "paid" ? "PAGADO" : o.payment_status}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-xs" data-testid={`admin-order-customer-${i}`}>
                          {o.customer ? (
                            <>
                              <span className="block font-semibold text-sm">{o.customer.name || "—"}</span>
                              <span className="block text-zinc-500">{o.customer.email}</span>
                              <span className="block text-zinc-500">{o.customer.phone}</span>
                            </>
                          ) : "—"}
                        </td>
                        <td className="px-5 py-3 text-xs text-zinc-600 max-w-[220px]" data-testid={`admin-order-address-${i}`}>
                          {o.shipping?.name && <span className="block font-medium">{o.shipping.name}</span>}
                          {addrStr || "—"}
                        </td>
                        <td className="px-5 py-3 text-xs text-zinc-400">{o.utm && Object.keys(o.utm).length ? `${o.utm.utm_source || ""} ${o.utm.utm_campaign || ""}`.trim() || "—" : "—"}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
